'use client';

import { useState, useCallback, useEffect, useMemo } from 'react';
import { SlashTriggerContext } from '@/types/slash-commands';
import { COMMANDS, SlashCommand } from '@/lib/slash-commands/registry'; // Import registry

// Remove local Command interface definition
// export interface Command { ... }

// SlashTriggerContext is imported from types

// Remove local ALL_COMMANDS definition
// const ALL_COMMANDS: Command[] = [ ... ];

export function useSlashCommandMenu(currentTaskType: 'task1' | 'task2') {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuTargetElement, setMenuTargetElement] =
    useState<HTMLTextAreaElement | null>(null);
  const [filterText, setFilterText] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [triggerContext, setTriggerContext] =
    useState<SlashTriggerContext | null>(null);

  // Filter commands using the registry's contextFilter
  const availableCommands = useMemo(() => {
    if (!triggerContext) return [];
    return COMMANDS.filter(
      (cmd) =>
        cmd.taskTypes.includes(currentTaskType) &&
        cmd.contextFilter(triggerContext)
    );
  }, [triggerContext, currentTaskType]);

  // This filtering logic remains the same
  const filteredCommands = useMemo(() => {
    if (!filterText) {
      return availableCommands;
    }
    const lowerFilter = filterText.toLowerCase();
    return availableCommands.filter(
      (cmd) =>
        cmd.name.toLowerCase().includes(lowerFilter) ||
        cmd.description?.toLowerCase().includes(lowerFilter)
    );
  }, [availableCommands, filterText]);

  // ... rest of the hook remains largely the same ...
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredCommands]);

  const openMenu = useCallback((context: SlashTriggerContext) => {
    setMenuTargetElement(context.targetElement);
    setTriggerContext(context);
    setFilterText('');
    setSelectedIndex(0);
    setIsMenuOpen(true);
  }, []);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    setMenuTargetElement(null);
    setFilterText('');
    setSelectedIndex(0);
    setTriggerContext(null);
  }, []);

  const updateFilter = useCallback(
    (text: string) => {
      if (isMenuOpen) {
        setFilterText(text);
      }
    },
    [isMenuOpen]
  );

  const selectCommandByIndex = useCallback(
    (
      index: number,
      // The executeCallback signature changes slightly, it now only needs commandId
      // as the context is already stored in the triggerContext state
      executeCallback: (commandId: string) => void
    ) => {
      const command = filteredCommands[index];
      if (command) {
        console.log(
          '[Slash Menu Hook] Selecting command:',
          command.id,
          'with context:',
          triggerContext
        );
        // Pass only commandId, the manager hook will use triggerContext
        executeCallback(command.id);
        closeMenu();
      }
    },
    [filteredCommands, closeMenu, triggerContext]
  );

  const handleMenuKeyDown = useCallback(
    (
      event: KeyboardEvent | React.KeyboardEvent,
      executeCallback: (commandId: string) => void // Update signature
    ) => {
      if (!isMenuOpen || filteredCommands.length === 0) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedIndex((prevIndex) =>
          prevIndex >= filteredCommands.length - 1 ? 0 : prevIndex + 1
        );
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedIndex((prevIndex) =>
          prevIndex <= 0 ? filteredCommands.length - 1 : prevIndex - 1
        );
      } else if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        selectCommandByIndex(selectedIndex, executeCallback);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
      }
    },
    [
      isMenuOpen,
      filteredCommands,
      selectedIndex,
      closeMenu,
      selectCommandByIndex,
    ]
  );

  return {
    isMenuOpen,
    menuTargetElement,
    filterText,
    // Return SlashCommand[] type from registry
    filteredCommands: filteredCommands as SlashCommand[],
    selectedIndex,
    openMenu,
    closeMenu,
    updateFilter,
    handleMenuKeyDown,
    setSelectedIndex,
    triggerContext,
  };
}
